import React, { useState, useEffect } from 'react';
import { APP_CONFIG, AI_BOT_NAMES, ACTIVITY_TYPES } from '../config';
import { TickerTape, MiniChart } from './TradingWidgets';
import { parseError } from '../utils/errorParser';
import { sendTelegramNotification } from '../utils/telegramNotify';
import './Home.css';

function Home({ 
  userId = '', 
  userData = {}, 
  userInvestments = [], 
  tokenBalance = '0', 
  userActivities = [], 
  totalActivityCount = 0,
  invest, 
  startTrade, 
  claimTradeROI, 
  claimRewards, 
  refreshData, 
  addNotification = () => {},
  onViewAllActivities, 
  contract
}) {
  const [showInvestModal, setShowInvestModal] = useState(false);
  const [investAmount, setInvestAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [incomeBreakdown, setIncomeBreakdown] = useState({
    profit: 0, levelIncome: 0, directReferral: 0, clubReward: 0
  });
  const [roiTimeframe, setRoiTimeframe] = useState(86400); // Default 24 hours
  const [timers, setTimers] = useState({});

  const referralLink = `${window.location.origin}?ref=${userId}`;

  // Calculate income breakdown from activities
  useEffect(() => {
    if (userActivities && Array.isArray(userActivities) && userActivities.length > 0) {
      const breakdown = { profit: 0, levelIncome: 0, directReferral: 0, clubReward: 0 };
      userActivities.forEach(act => {
        const amount = parseFloat(act.amount) || 0;
        switch (act.activityType) {
          case 2: breakdown.profit += amount; break;
          case 3: breakdown.levelIncome += amount; break;
          case 5: case 6: breakdown.directReferral += amount; break;
          case 7: breakdown.clubReward += amount; break;
          default: break;
        }
      });
      setIncomeBreakdown(breakdown);
    }
  }, [userActivities]);

  // Fetch ROI timeframe from contract
  useEffect(() => {
    const fetchTimeframe = async () => {
      if (contract) {
        try {
          const tf = await contract.getROITimeframe();
          setRoiTimeframe(tf.toNumber());
        } catch (e) { 
          console.log('Using default ROI timeframe');
        }
      }
    };
    fetchTimeframe();
  }, [contract]);

  // Timer effect - using lastTradeStart from contract with localStorage fallback
  useEffect(() => {
    if (!userInvestments || !Array.isArray(userInvestments)) return;
    
    const calculateTimers = () => {
      const newTimers = {};
      
      userInvestments.forEach((inv, index) => {
        if (inv && inv.active && inv.tradeActive) {
          let startTime = parseInt(inv.lastTradeStart) || 0;
          
          // Fallback to localStorage if contract doesn't have lastTradeStart (old contract)
          if (startTime === 0) {
            const storageKey = `trade_start_${userId}_${index}`;
            const savedStartTime = localStorage.getItem(storageKey);
            if (savedStartTime) {
              startTime = parseInt(savedStartTime);
            }
          }
          
          if (startTime > 0) {
            const now = Math.floor(Date.now() / 1000);
            const elapsed = now - startTime;
            const remaining = Math.max(0, roiTimeframe - elapsed);
            const progress = Math.min((elapsed / roiTimeframe) * 100, 100);
            
            newTimers[index] = {
              remaining,
              elapsed,
              progress,
              isComplete: remaining === 0
            };
          }
        }
      });
      
      setTimers(newTimers);
    };
    
    calculateTimers();
    const interval = setInterval(calculateTimers, 1000);
    return () => clearInterval(interval);
  }, [userInvestments, roiTimeframe, userId]);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    addNotification('success', 'Referral link copied!');
  };

  const handleInvest = async () => {
    const amount = parseFloat(investAmount);
    if (!amount || amount < APP_CONFIG.minInvestment) {
      addNotification('error', `Minimum investment is $${APP_CONFIG.minInvestment}`);
      return;
    }
    if (amount % APP_CONFIG.investmentMultiple !== 0) {
      addNotification('error', `Amount must be multiple of $${APP_CONFIG.investmentMultiple}`);
      return;
    }
    setIsLoading(true);
    try {
      await invest(investAmount);
      addNotification('success', 'Investment successful! AI Bot assigned.');
      
      // Send Telegram notification
      sendTelegramNotification('investment', {
        amount: investAmount,
        botName: getBotName(userInvestments?.length || 0)
      }, userId);
      
      setInvestAmount('');
      setShowInvestModal(false);
    } catch (error) {
      addNotification('error', parseError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrade = async (index) => {
    setLoadingAction(`start-${index}`);
    try {
      await startTrade(index);
      
      // Save start time to localStorage as fallback for old contract
      const storageKey = `trade_start_${userId}_${index}`;
      const startTime = Math.floor(Date.now() / 1000);
      localStorage.setItem(storageKey, startTime.toString());
      
      addNotification('success', 'AI Trading activated! Timer started.');
      
      // Send Telegram notification
      const inv = userInvestments[index];
      sendTelegramNotification('startTrade', {
        amount: inv?.amount || 0,
        botName: getBotName(index)
      }, userId);
      
    } catch (error) {
      addNotification('error', parseError(error));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleTakeProfit = async (index) => {
    setLoadingAction(`profit-${index}`);
    try {
      const inv = userInvestments[index];
      const profitBefore = parseFloat(inv?.profitEarned || 0);
      
      await claimTradeROI(index);
      
      // Clear the timer from localStorage after claiming
      const storageKey = `trade_start_${userId}_${index}`;
      localStorage.removeItem(storageKey);
      
      addNotification('success', 'Profit claimed successfully!');
      
      // Calculate estimated profit (0.5% - 1% of investment)
      const investAmount = parseFloat(inv?.amount || 0);
      const estimatedProfit = investAmount * (inv?.profitRate || 0.75) / 100;
      
      // Send Telegram notification
      sendTelegramNotification('takeProfit', {
        profit: estimatedProfit,
        rate: inv?.profitRate || '0.5-1',
        amount: investAmount,
        totalEarned: profitBefore + estimatedProfit
      }, userId);
      
    } catch (error) {
      addNotification('error', parseError(error));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleWithdraw = async () => {
    if (!userData || parseFloat(userData.pendingRewards) <= 0) {
      addNotification('error', 'No rewards to withdraw');
      return;
    }
    
    const withdrawAmount = parseFloat(userData.pendingRewards);
    const fee = withdrawAmount * 0.1;
    const received = withdrawAmount - fee;
    
    setLoadingAction('withdraw');
    try {
      await claimRewards();
      addNotification('success', 'Withdrawal successful!');
      
      // Send Telegram notification
      sendTelegramNotification('withdraw', {
        amount: withdrawAmount,
        fee: fee,
        received: received
      }, userId);
      
    } catch (error) {
      addNotification('error', parseError(error));
    } finally {
      setLoadingAction(null);
    }
  };

  const getBotName = (index) => AI_BOT_NAMES[index % AI_BOT_NAMES.length];
  
  const formatTime = (ts) => new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const formatTimer = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const totalIncome = parseFloat(userData?.totalEarned || 0);
  const pendingIncome = parseFloat(userData?.pendingRewards || 0);
  const totalInvested = parseFloat(userData?.totalInvestment || 0);

  return (
    <div className="home-page">
      {/* TradingView Ticker Tape */}
      <TickerTape />

      {/* Welcome Header */}
      <div className="welcome-header">
        <h1 className="welcome-title">Welcome, Trader!</h1>
        <div className="user-id-badge">ID: {userId}</div>
      </div>

      {/* Hero with Bot Animation */}
      <div className="hero-section">
        <div className="bot-animation-container">
          <div className="bot-rings">
            <div className="bot-ring ring-1"></div>
            <div className="bot-ring ring-2"></div>
            <div className="bot-ring ring-3"></div>
          </div>
          <div className="bot-center">
            <span className="bot-icon">🤖</span>
          </div>
        </div>
        <div className="bot-status">
          <span className="status-dot active"></span>
          <span>AI Trading Active</span>
        </div>
      </div>

      {/* User Stats Cards */}
      <div className="user-stats-grid">
        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-info">
            <span className="stat-label">Total Invested</span>
            <span className="stat-value">${totalInvested.toFixed(2)}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-info">
            <span className="stat-label">Total Earned</span>
            <span className="stat-value positive">${totalIncome.toFixed(2)}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <span className="stat-label">Available</span>
            <span className="stat-value accent">${pendingIncome.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Income Breakdown */}
      <div className="income-breakdown-section">
        <h3 className="breakdown-title">📊 Income Breakdown</h3>
        <div className="breakdown-grid">
          <div className="breakdown-card">
            <span className="bc-icon">📈</span>
            <span className="bc-label">Trading Profit</span>
            <span className="bc-value green">${incomeBreakdown.profit.toFixed(2)}</span>
          </div>
          <div className="breakdown-card">
            <span className="bc-icon">🎯</span>
            <span className="bc-label">Level Income</span>
            <span className="bc-value purple">${incomeBreakdown.levelIncome.toFixed(2)}</span>
          </div>
          <div className="breakdown-card">
            <span className="bc-icon">👥</span>
            <span className="bc-label">Direct Referral</span>
            <span className="bc-value cyan">${incomeBreakdown.directReferral.toFixed(2)}</span>
          </div>
          <div className="breakdown-card">
            <span className="bc-icon">🏆</span>
            <span className="bc-label">Club Reward</span>
            <span className="bc-value gold">${incomeBreakdown.clubReward.toFixed(2)}</span>
          </div>
        </div>
        <div className="breakdown-summary">
          <div className="summary-item">
            <span>Pending</span>
            <span className="summary-value">${pendingIncome.toFixed(2)}</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item">
            <span>Total</span>
            <span className="summary-value highlight">${totalIncome.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <div className="referral-section">
        <div className="referral-header">
          <span className="referral-label">Your Referral Link</span>
          <span className="referral-id">ID: {userId}</span>
        </div>
        <div className="referral-link-box">
          <input type="text" value={referralLink} readOnly className="referral-input" />
          <button className="copy-btn" onClick={copyReferralLink}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
          </button>
        </div>
      </div>

      {/* Withdraw Section */}
      <div className="withdraw-section">
        <div className="withdraw-info">
          <span className="withdraw-label">Available to Withdraw</span>
          <span className="withdraw-amount">${pendingIncome.toFixed(2)}</span>
          <span className="withdraw-fee">10% fee</span>
        </div>
        <button className="withdraw-btn-small" onClick={handleWithdraw} disabled={loadingAction === 'withdraw' || pendingIncome <= 0}>
          {loadingAction === 'withdraw' ? <span className="btn-spinner" /> : 'WITHDRAW'}
        </button>
      </div>

      {/* Invest More Button - Full Width Section */}
      <button className="invest-more-btn" onClick={() => setShowInvestModal(true)}>
        <span className="invest-icon">➕</span>
        <span>INVEST MORE</span>
      </button>

      {/* Chart */}
      <div className="chart-section">
        <h3 className="section-title">📈 BNB/USDT Chart</h3>
        <MiniChart symbol="BINANCE:BNBUSDT" />
      </div>

      {/* Active AI Traders */}
      {userInvestments && userInvestments.length > 0 && (
        <div className="traders-section">
          <h2 className="section-title">🤖 Active AI Traders</h2>
          <div className="traders-list">
            {userInvestments.map((inv, index) => {
              if (!inv) return null;
              
              const amount = parseFloat(inv.amount) || 0;
              const profitEarned = parseFloat(inv.profitEarned) || 0;
              const otherIncome = parseFloat(inv.otherIncomeEarned) || 0;
              const totalEarned = profitEarned + otherIncome;
              const profitCeiling = amount * (APP_CONFIG.profitCeiling / 100);
              const totalCeiling = amount * (APP_CONFIG.totalCeiling / 100);
              const profitProgress = profitCeiling > 0 ? (profitEarned / profitCeiling) * 100 : 0;
              const totalProgress = totalCeiling > 0 ? (totalEarned / totalCeiling) * 100 : 0;
              
              // Timer data from state
              const timerData = timers[index];
              const hasTimer = inv.tradeActive && timerData;
              const isTimerComplete = hasTimer && timerData.isComplete;

              return (
                <div key={index} className={`trader-card ${inv.active ? 'active' : 'completed'}`}>
                  <div className="trader-header">
                    <div className="bot-info">
                      <div className="bot-avatar-small">🤖</div>
                      <div className="bot-details">
                        <span className="bot-name">{getBotName(index)}</span>
                        <span className="bot-status-text">
                          {inv.active ? (inv.tradeActive ? '🟢 Trading' : '🟡 Ready') : '⚫ Completed'}
                        </span>
                      </div>
                    </div>
                    <div className="trader-amount">${amount.toFixed(2)}</div>
                  </div>

                  <div className="trader-stats">
                    <div className="ts-item">
                      <span className="ts-label">RATE</span>
                      <span className="ts-value">{inv.profitRate || 0}%</span>
                    </div>
                    <div className="ts-item">
                      <span className="ts-label">PROFIT</span>
                      <span className="ts-value positive">${profitEarned.toFixed(2)}</span>
                    </div>
                    <div className="ts-item">
                      <span className="ts-label">OTHER</span>
                      <span className="ts-value">${otherIncome.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Timer Display - Only show when trade is active */}
                  {hasTimer && (
                    <div className={`trade-timer ${isTimerComplete ? 'complete' : ''}`}>
                      <div className="timer-ring">
                        <svg viewBox="0 0 100 100">
                          <circle className="timer-bg" cx="50" cy="50" r="45" />
                          <circle 
                            className="timer-progress" 
                            cx="50" cy="50" r="45"
                            style={{ strokeDashoffset: 283 - (283 * timerData.progress / 100) }}
                          />
                        </svg>
                        <div className="timer-content">
                          {isTimerComplete ? (
                            <>
                              <span className="timer-icon">✓</span>
                              <span className="timer-value ready">Ready!</span>
                              <span className="timer-label">CLAIM NOW</span>
                            </>
                          ) : (
                            <>
                              <span className="timer-icon">⏱️</span>
                              <span className="timer-value">{formatTimer(timerData.remaining)}</span>
                              <span className="timer-label">Next Profit</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Progress Bars */}
                  <div className="ceiling-bar">
                    <div className="ceiling-header">
                      <span className="ceiling-label">Profit Limit (200%)</span>
                      <span className="ceiling-value">{Math.min(profitProgress, 100).toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar purple">
                      <div className="progress-fill" style={{ width: `${Math.min(profitProgress, 100)}%` }} />
                    </div>
                  </div>

                  <div className="ceiling-bar">
                    <div className="ceiling-header">
                      <span className="ceiling-label">Total Ceiling (300%)</span>
                      <span className="ceiling-value">{Math.min(totalProgress, 100).toFixed(1)}%</span>
                    </div>
                    <div className="progress-bar cyan">
                      <div className="progress-fill" style={{ width: `${Math.min(totalProgress, 100)}%` }} />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {inv.active && inv.investmentType !== 2 && (
                    <div className="trader-actions">
                      {!inv.tradeActive ? (
                        <button className="action-btn start-trade" onClick={() => handleStartTrade(index)} disabled={loadingAction === `start-${index}`}>
                          {loadingAction === `start-${index}` ? <span className="btn-spinner" /> : '🚀 Start Trade'}
                        </button>
                      ) : isTimerComplete ? (
                        <button className="action-btn take-profit" onClick={() => handleTakeProfit(index)} disabled={loadingAction === `profit-${index}`}>
                          {loadingAction === `profit-${index}` ? <span className="btn-spinner" /> : '💰 Take Profit'}
                        </button>
                      ) : (
                        <div className="trading-in-progress">
                          <span className="pulse-dot"></span>
                          <span>Trading in progress...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activities */}
      <div className="activities-section">
        <div className="section-header">
          <h2 className="section-title">📋 Recent Activities</h2>
          {totalActivityCount > 5 && <button className="view-all-btn" onClick={onViewAllActivities}>View All</button>}
        </div>
        {!userActivities || userActivities.length === 0 ? (
          <div className="empty-activities">No activities yet</div>
        ) : (
          <div className="activities-list">
            {[...userActivities].reverse().slice(0, 5).map((act, i) => {
              if (!act) return null;
              const type = ACTIVITY_TYPES[act.activityType] || { label: 'Unknown', icon: '❓', color: '#888' };
              return (
                <div key={i} className="activity-item" style={{ '--accent': type.color }}>
                  <div className="activity-icon">{type.icon}</div>
                  <div className="activity-info">
                    <span className="activity-type">{type.label}</span>
                    <span className="activity-time">{formatTime(act.timestamp)}</span>
                  </div>
                  <span className="activity-amount" style={{ color: type.color }}>+${parseFloat(act.amount || 0).toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Investment Modal */}
      {showInvestModal && (
        <div className="modal-overlay" onClick={() => setShowInvestModal(false)}>
          <div className="invest-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowInvestModal(false)}>✕</button>
            <div className="modal-header">
              <div className="modal-icon">🤖</div>
              <h2>New Investment</h2>
              <p>Assign an AI Trader to your funds</p>
            </div>
            <div className="modal-stats">
              <div className="ms-item">
                <span className="ms-label">Daily Profit</span>
                <span className="ms-value">0.5% - 1%</span>
              </div>
              <div className="ms-item">
                <span className="ms-label">Max Return</span>
                <span className="ms-value">300%</span>
              </div>
            </div>
            <div className="input-group">
              <label>Investment Amount (USDT)</label>
              <div className="input-wrapper">
                <input type="number" placeholder={`Min: $${APP_CONFIG.minInvestment}`} value={investAmount} onChange={(e) => setInvestAmount(e.target.value)} step={APP_CONFIG.investmentMultiple} min={APP_CONFIG.minInvestment} />
                <span className="input-suffix">USDT</span>
              </div>
            </div>
            <div className="quick-amounts">
              {[10, 50, 100, 500, 1000].map(amt => (
                <button key={amt} className="quick-btn" onClick={() => setInvestAmount(amt.toString())}>${amt}</button>
              ))}
            </div>
            <div className="wallet-balance">Balance: ${parseFloat(tokenBalance || 0).toFixed(2)} USDT</div>
            <button className="invest-btn" onClick={handleInvest} disabled={isLoading}>
              {isLoading ? <><span className="btn-spinner" />Processing...</> : 'Invest Now'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
